import Button from "shared/components/Button";
import { ReactComponent as CrossSVG } from "assets/icons/cross.svg";
import { ReactComponent as TickHard } from "assets/icons/tick-hard.svg";
import { ReactComponent as ArrowCurl } from "assets/icons/arrow-curl.svg";
import { Tooltip } from "react-tooltip";
import { DELAY_SHOW } from "shared/helpers/reactTooltip";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { setMeasureMode } from "features/krpano/details-slice";
import { Transition } from "react-transition-group";
import { TransitionProps } from "react-transition-group/Transition";
import { useRef } from "react";
import { useMeasureButtons } from "./useMeasureButtons";

/**
 * `$transitionShortMedium` in,
 * `$transitionShort` out
 */
const TIMEOUTS: TransitionProps["timeout"] = {
  enter: 0,
  appear: 450,
  exit: 300,
};

interface MeasureButtonsProps {}

/**
 * The buttons to show when in measure-mode.
 */
const MeasureButtons: React.FC<MeasureButtonsProps> = () => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const measureMode = useAppSelector((state) => state.krpano.measureMode);
  const dispatch = useAppDispatch();

  const { undoDisabled, confirmDisabled } = useMeasureButtons();

  return (
    <Transition
      nodeRef={nodeRef}
      in={measureMode === "IN_PROGRESS"}
      timeout={TIMEOUTS}
      mountOnEnter
      // Can unmount, but I consider this a key component of the UI
      // unmountOnExit
    >
      {/* Measure Buttons */}
      {(transitionState) => (
        <div
          ref={nodeRef}
          className={`KeyButtons DisplayFlex LargeGap ${transitionState}`}
        >
          {/* Confirm */}
          <div data-tooltip-id="KEY_BUTTON_MEASURE_CONFIRM">
            <Button
              colour="Theme"
              additionalClassNames="PrimaryButton SVGButton NoBorder"
              disabledClass="DisabledOpacity"
              disabled={confirmDisabled}
              onClick={() => {
                window.krpano.call("handle_measure_confirm();");
              }}
              children={
                <TickHard
                  height={20}
                  width={20}
                  role="button"
                  aria-label="Confirm"
                />
              }
            />
            <Tooltip
              id="KEY_BUTTON_MEASURE_CONFIRM"
              place="top"
              className="ToolTip"
              delayShow={DELAY_SHOW}
              children="Confirm"
            />
          </div>
          {/* Undo */}
          <div data-tooltip-id="KEY_BUTTON_MEASURE_UNDO">
            <Button
              colour="Theme"
              additionalClassNames="PrimaryButton SVGButton NoBorder"
              disabledClass="DisabledOpacity"
              disabled={undoDisabled}
              onClick={() => {
                window.krpano.call("handle_undo_last_measurepoint();");
              }}
              children={
                <ArrowCurl
                  height={20}
                  width={20}
                  role="button"
                  aria-label="Undo"
                />
              }
            />
            <Tooltip
              id="KEY_BUTTON_MEASURE_UNDO"
              place="top"
              className="ToolTip"
              delayShow={DELAY_SHOW}
              children="Undo"
            />
          </div>
          {/* Cancel */}
          <div data-tooltip-id="KEY_BUTTON_MEASURE_CANCEL">
            <Button
              colour="Theme"
              additionalClassNames="PrimaryButton SVGButton NoBorder"
              disabledClass="DisabledOpacity"
              onClick={() => {
                dispatch(setMeasureMode(null));
              }}
              children={
                <CrossSVG
                  height={20}
                  width={20}
                  role="button"
                  aria-label="Cancel"
                />
              }
            />
            <Tooltip
              id="KEY_BUTTON_MEASURE_CANCEL"
              place="top"
              className="ToolTip"
              delayShow={DELAY_SHOW}
              children="Cancel"
            />
          </div>
        </div>
      )}
    </Transition>
  );
};

export default MeasureButtons;
